import { ApolloCache, ApolloError, useApolloClient } from "@apollo/client";
import { useRouter } from "expo-router";

import useMobileScreen from "./useMobileScreen";
import useRecordUserGoal, { GoalType } from "./useRecordUserGoal";
import { useToastNotification } from "./useToastNotification";

import { useCurrentUser } from "@/features/auth";
import {
  ActivityFeedFragment,
  ListActivitiesFeedDocument,
  ListActivitiesFeedQuery,
  MarkActivitiesAsReadMutation,
  NotesGetNoteDocument,
  type NotesGetNoteQuery,
  type NotesGetNoteQueryVariables,
  useMarkActivitiesAsReadMutation,
} from "@/generated/graphql";
import { handleDeeplinkNavigation } from "@/utils/handleDeepLinkActivity";
import { isNoteActivity } from "@/utils/isNoteActivity";

/**
 * Activity types whose targetId is a player ID.
 * All other activity types either use metadata.playerId, require a note query,
 * or don't navigate to a player page at all (org/user activities).
 */
const PLAYER_TARGET_ACTIVITY_TYPES = new Set([
  "PLAYER_ADDED_TO_WATCHLIST",
  "PLAYER_REMOVED_FROM_WATCHLIST",
  "PLAYER_RATING_UPDATED",
  "PLAYER_ANALYSIS_UPDATED",
  "MANUAL_PLAYER_CREATED",
  "MANUAL_PLAYER_UPDATED",
  "MANUAL_PLAYER_DELETED",
  "MANUAL_PLAYER_RESTORED",
  "MANUAL_PLAYER_DATA_TRANSFERRED",
]);

/**
 * Custom hook for handling activity deep links and marking activities as read.
 * @remarks
 * Provides functions to navigate to activities and mark them as read in the cache.
 * Updates the Apollo cache optimistically when marking activities as read.
 * For note activities (including CONTENT_SHARED with contentType "NOTE"),
 * resolves the player ID from the note when metadata.playerId is unavailable,
 * since targetId points to the note rather than the player (SE-3989, SE-3990).
 * @returns Functions for handling activity taps and marking notifications as read
 * @example
 * ```typescript
 * const { handleActivityTapped, markActivityNotification } = useActivityDeepLink();
 *
 * // Handle activity tap and navigate
 * handleActivityTapped(activityItem);
 *
 * // Mark activity as read without navigation
 * markActivityNotification(activityId);
 * ```
 */
const useActivityDeepLink = () => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const toast = useToastNotification();
  const isSmallScreen = useMobileScreen();
  const apolloClient = useApolloClient();

  const recordOpenDeepLinkGoal = useRecordUserGoal({
    goalType: GoalType.OpenActivityDeepLink,
    value: currentUser?.id ?? "",
  });

  const [markActivityAsRead] = useMarkActivitiesAsReadMutation({
    onError: (err: ApolloError) =>
      toast.show({
        action: "danger",
        description: err.message,
      }),
  });

  const markActivityNotification = (_activityId: string) => {
    markActivityAsRead({
      variables: {
        input: {
          activityIds: [_activityId],
        },
      },
    });
  };

  /**
   * Resolve playerId for an activity, fetching the note when needed.
   * For note activities (including CONTENT_SHARED with contentType "NOTE"),
   * targetId is the note ID — not the player ID — so we query the note to
   * extract its playerId when metadata lacks one (SE-3990).
   * @param activityType - The activity type code
   * @param metadata - Activity metadata that may contain playerId or noteId
   * @param targetId - The activity's targetId (note ID for note activities)
   * @returns The resolved player ID, or undefined if unresolvable
   */
  const resolvePlayerId = async (
    activityType: string,
    metadata: Record<string, unknown> | null,
    targetId: string | null | undefined
  ): Promise<string | undefined> => {
    const metadataPlayerId = metadata?.playerId as string | undefined;

    if (metadataPlayerId) return metadataPlayerId;

    // For player-scoped activities, targetId IS the player ID
    if (PLAYER_TARGET_ACTIVITY_TYPES.has(activityType))
      return targetId || undefined;

    const noteActivity = isNoteActivity(activityType, metadata);

    // For non-note, non-player activities (org/user events, scout reports),
    // targetId is NOT a player ID — don't use it as a fallback
    if (!noteActivity) return undefined;

    // For note activities, resolve the note ID to fetch the player
    const noteId =
      (metadata?.noteId as string | undefined) || targetId || undefined;
    if (!noteId) return undefined;

    try {
      const result = await apolloClient.query<
        NotesGetNoteQuery,
        NotesGetNoteQueryVariables
      >({
        query: NotesGetNoteDocument,
        variables: { id: noteId },
      });

      return result.data?.getNote?.playerId || undefined;
    } catch (error) {
      console.warn("Failed to resolve player from note activity:", error);
      return undefined;
    }
  };

  const handleActivityTapped = async (activityItem: ActivityFeedFragment) => {
    if (activityItem) {
      const { activity, activityId } = activityItem ?? {};
      markActivityAsRead({
        variables: {
          input: {
            activityIds: [activityId],
          },
        },
        update(
          cache: ApolloCache<MarkActivitiesAsReadMutation>,
          { data: responseData, errors }
        ) {
          if (!responseData || errors) return;

          const existingData: ListActivitiesFeedQuery | null = cache.readQuery({
            query: ListActivitiesFeedDocument,
          });

          const matchingEdge = existingData?.listMyActivities?.edges?.find(
            edge => edge.node.activityId === activityId
          );

          if (matchingEdge) {
            const nodeCacheId = cache.identify(matchingEdge.node);
            if (nodeCacheId) {
              cache.modify({
                id: nodeCacheId,
                fields: {
                  isRead() {
                    return true;
                  },
                },
              });
            }
          }
        },
      });

      recordOpenDeepLinkGoal();
      const executeAction = isSmallScreen ? router.push : router.navigate;

      const metadata = activity?.metadata as Record<string, unknown> | null;
      const activityType = activity?.activityType?.code ?? "";
      const playerId = await resolvePlayerId(
        activityType,
        metadata,
        activity?.targetId
      );

      const activityInfo = {
        activityType,
        playerId,
        targetId: activity?.targetId || undefined,
        // SE-5196: share activities navigate to this server-provided link
        deepLink: activity?.deepLink || undefined,
      };

      handleDeeplinkNavigation(activityInfo, executeAction);
    } else {
      console.warn("Invalid or unsupported deeplink");
    }
  };

  return {
    handleActivityTapped,
    markActivityNotification,
  };
};

export default useActivityDeepLink;
